import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import queryString from 'query-string'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Descriptions, Modal, Result, Switch } from 'antd'
import { useMountedRef } from '../../../_utils/use-mounted-ref'
import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { useLoadableDataState } from '../../../hooks/use-loadable-data-state'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import { handleResponseErrorMessage } from '../../../_utils/response-error-utils'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'
import { GetSlackDomainIntegrationResponse, UpdateDomainIntegrationRequest } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'

const SlackIntegration = (props: IntegrationProps<GetSlackDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const [toggleActionLoading, setToggleActionLoading] = useState(false)

    const history = useHistory()
    const runIfMounted = useMountedRef()[1]
    const [value, setValue] = useLoadableDataState({
        loading: false,
        data: integration,
    })
    const svc = useService<DomainIntegrationService>(DomainIntegrationService)

    const qs = queryString.parse(location.search)
    React.useEffect(() => {
        const processOauthCallback = async (domainId: number, _oauthState: string) => {
            setPrimaryActionLoading(true)

            setValue({
                loading: true,
                data: {
                    ...(value.data ?? ({} as any)),
                    isActive: true,
                    data: {
                        teamName: qs.team_name,
                        incomingWebhook: {
                            channel: qs.channel,
                        },
                    },
                },
            })

            Modal.destroyAll()
            const modal = Modal.success({
                className: 'platform-integration-modal completing-oauth-dialog',
                title: 'Integration Finalizing',
                icon: <span className="slack-logo-color" />,
                okText: 'Complete',
                cancelText: 'Cancel',
                cancelButtonProps: {
                    className: 'platform-integration-modal-action-cancel',
                    type: 'text',
                },
                footer: null,
                content: (
                    <div>
                        <Loading3QuartersOutlined spin={true} />
                    </div>
                ),
            } as any)

            const res = await svc.completeIntegrationSetup(domainId, DomainIntegrationProviders.SLACK, qs, {
                cancellationKey: 'slack-integration',
            })
            setPrimaryActionLoading(true)

            if (!res.ok) {
                modal.update({
                    className: 'platform-integration-modal failed-oauth-dialog',
                    title: 'Integration Failed',
                    content: (
                        <Result
                            status="warning"
                            title={false}
                            subTitle="An error occurred during your integration and we were unable to complete the setup."
                        />
                    ),
                    onOk: () => {
                        history.replace({ hash: 'integrations', search: '' })
                        modal.destroy()
                    },
                })

                return
            }

            modal.update({
                className: 'platform-integration-modal completed-oauth-dialog',
                title: 'Integration Completed',
                content: (
                    <Result
                        status="success"
                        title={false}
                        subTitle={`Team name: ${res.data?.data?.team_name}; Channel: ${res.data?.data?.incoming_webhook?.channel}`}
                    />
                ),
                okText: 'Close',
                onOk: () => {
                    history.replace({ hash: 'integrations', search: '' })
                    modal.destroy()
                    simpleNotification('success', 'Integration successfully completed.')

                    // @todo - can we get the integration here and run "add-integration" instead?
                    dispatch({ type: 'request-integrations-reload' })
                },
            })
        }

        if (mode === 'add' && domain?.id && qs.provider === DomainIntegrationProviders.SLACK && qs.state) {
            processOauthCallback(domain.id, qs.state.toString())
        }
    }, [mode, domain?.id, qs.state, qs.provider])

    const createIntegration = async () => {
        if (loading) return
        setPrimaryActionLoading(true)

        const redirectUrl = await svc.fetchIntegrationRedirectUrl('slack', domain.id)

        if (!redirectUrl.ok) {
            const err = redirectUrl.error as any
            handleResponseErrorMessage(err)
        } else {
            const { authUrl } = redirectUrl.data!
            window.location.href = authUrl
        }
    }

    const removeIntegration = async () => {
        if (loading || !integration) return
        setPrimaryActionLoading(true)

        const { channel, configuration_url } = integration.data.incoming_webhook

        Modal.confirm({
            className: 'platform-integration-modal remove-integration-dialog',
            title: 'Remove Integration',
            icon: <span className="slack-logo-color" />,
            okText: 'Complete',
            okButtonProps: {
                className: 'platform-integration-modal-action-ok action-type-remove',
                type: 'text',
            },
            cancelText: 'Cancel',
            cancelButtonProps: {
                className: 'platform-integration-modal-action-cancel',
                type: 'text',
            },
            content: (
                <div>
                    <p>
                        Performing this step will remove the Slack integration for channel <b>{channel}</b> from the
                        platform. To completely remove Slack access visit the following URL:
                    </p>
                    <p className="config-url">
                        <a href={configuration_url} target="_blank">
                            {configuration_url}
                        </a>
                    </p>
                    <p>
                        Access can be modified from the <b>Authorizations</b> panel, or completely removed by using the{' '}
                        <b>Remove App</b> button, on the <b>Configuration</b> tab.
                    </p>
                </div>
            ),
            onOk: async () => {
                const res = await svc.removeIntegration(domain.id, value.data!.id!)

                if (res.ok) {
                    simpleNotification('success', 'Integration successfully removed.')
                    dispatch({ type: 'rem-integration', integration })
                } else {
                    setValue({
                        ...value,
                        loading: false,
                    })
                }
            },
            onCancel: async () => {
                setPrimaryActionLoading(false)
                setValue({
                    ...value,
                    loading: false,
                })
            },
        })
    }

    const toggleIntegration = async (enabled: boolean) => {
        if (loading || !integration) return
        setToggleActionLoading(true)

        const update = new UpdateDomainIntegrationRequest()
        update.id = integration.id
        update.is_active = enabled

        // @ts-ignore - @fixme - change updateById signature
        const res = await svc.updateById(domain.id, update)

        runIfMounted(() => {
            setToggleActionLoading(false)

            if (!res.ok || !res.data) {
                if (!res.cancelled) {
                    console.warn(res.error)

                    simpleNotification(
                        'error',
                        enabled ? 'Integration could not be enabled.' : 'Integration could not be disabled.',
                    )
                }

                return
            }

            simpleNotification(
                'success',
                enabled ? 'Integration successfully enabled.' : 'Integration successfully disabled.',
            )

            dispatch({ type: 'set-integration', integration: res.data })
        })
    }

    return (
        <PlatformIntegrationCard
            loading={loading}
            className={props.className}
            provider={DomainIntegrationProviders.SLACK}
            logo={<span className="slack-logo-color" />}
            mode={mode}
            isActive={integration?.is_active}
            actions={{
                primary: {
                    loading: primaryActionLoading,
                    onClick: () => {
                        if (!integration) {
                            createIntegration()
                        } else {
                            removeIntegration()
                        }
                    },
                },
            }}
        >
            {integration && (
                <Descriptions layout="horizontal" column={1}>
                    <Descriptions.Item className="slack-enabled-toggle" label="Enabled">
                        <Switch
                            size="small"
                            loading={toggleActionLoading}
                            checked={integration.is_active}
                            onChange={toggleIntegration}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="Team Name">{integration.data.team_name}</Descriptions.Item>
                    <Descriptions.Item label="Channel">{integration.data.incoming_webhook.channel}</Descriptions.Item>
                </Descriptions>
            )}
        </PlatformIntegrationCard>
    )
}

export default SlackIntegration
